import { Info, Plus, TriangleAlert, X } from "lucide-react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { distancia, hectareas, media as calcularMedia, numero } from "@/lib/formatoDeMercado";
import type { Comparable } from "@/lib/referencias";
import { cn } from "@/lib/utils";

interface PanelDeSeleccionProps {
  seleccionadas: Comparable[];
  onQuitar: (ficha: Comparable) => void;
  onVaciar: () => void;
  onAgregar: () => void;
  agregando: boolean;
  /** Cuántas referencias tiene ya el informe: las nuevas se SUMAN. */
  yaEnlazadas: number;
  informeId: number;
  /**
   * Cuántas fichas cumplen el filtro, para el contador "3 de 47".
   *
   * Un "3" solo no dice si se eligió casi todo o una de mil, que es
   * justamente lo que ubica la selección dentro del conjunto que se mira.
   */
  totalDelFiltro: number;
  /** La del predio TASADO, de la que sale el valor total derivado. */
  superficieDelPredioHa: number | null;
  /**
   * Los `origen_id` de esta pestaña que ya se dieron por buenos.
   *
   * Llega el conjunto ENTERO y no solo el de las seleccionadas: es el mismo que
   * usa la tarjeta para dibujar su botón, y partirlo en dos dejaría dos lugares
   * decidiendo qué está validado.
   */
  validadas: Set<number>;
}

/**
 * El panel de la derecha: qué se lleva el informe y cuánto dice.
 *
 * ES UN RESUMEN, NO UNA TASACIÓN. La media de acá es la de las fichas elegidas
 * SIN NINGUNA HOMOLOGACIÓN: no ajusta por superficie, ni por acceso, ni por
 * derechos de agua, que es lo que un tasador hace después dentro del informe.
 * El rótulo lo dice explícito porque una cifra grande y verde en un panel se
 * lee como una conclusión, y esta no lo es.
 *
 * ES LA MEDIA, Y NO LA MEDIANA DEL GRÁFICO. Son dos cifras distintas sobre dos
 * conjuntos distintos y conviene no confundirlas: la del gráfico resume el
 * FILTRO —miles de fichas que nadie eligió, donde un extremo no puede correr
 * una mediana— y esta resume LO ELEGIDO, que es el conjunto que entra al
 * informe. El rótulo de cada una dice cuál es.
 *
 * Existe igual porque sin él la selección es una lista de casillas: lo que el
 * tasador necesita saber mientras elige es si el conjunto que está armando
 * tiene sentido — si el rango es coherente o si acaba de meter un extremo que
 * lo va a distorsionar.
 */
export function PanelDeSeleccion({
  seleccionadas,
  onQuitar,
  onVaciar,
  onAgregar,
  agregando,
  yaEnlazadas,
  informeId,
  totalDelFiltro,
  superficieDelPredioHa,
  validadas,
}: PanelDeSeleccionProps) {
  const valores = seleccionadas.map((f) => f.uf_ha_terreno);
  const mediaUfHa = calcularMedia(valores);

  // Cuántas de las elegidas fueron revisadas. Se cuenta sobre LAS
  // SELECCIONADAS y no sobre todo el filtro: lo que importa acá es el conjunto
  // que está por entrar al informe, no cuánto se lleva revisado del catálogo.
  const seleccionadasValidadas = seleccionadas.filter((f) => validadas.has(f.origen_id)).length;
  const seleccionadasSinValidar = seleccionadas.length - seleccionadasValidadas;
  const conValor = valores.filter((v): v is number => v !== null && v > 0);

  /**
   * Lo que valdría el predio si el mercado elegido se aplicara TAL CUAL.
   *
   * Es la única cifra DERIVADA del panel, y por eso vive debajo de la bajada
   * que dice "sin ajustes de homologación": el ajuste por superficie, acceso o
   * derechos de agua viene después, dentro del informe, y puede moverla mucho.
   *
   * Sin superficie del predio se muestra "—" y NUNCA cero: un informe puede
   * estar `incompleto` (RF-E1 §8.3), y un cero se lee como una medición en vez
   * de como el dato que falta.
   */
  const valorTotalUf =
    mediaUfHa === null || superficieDelPredioHa === null || superficieDelPredioHa <= 0
      ? null
      : mediaUfHa * superficieDelPredioHa;

  const distancias = seleccionadas
    .map((f) => f.distancia_m)
    .filter((d): d is number => d !== null);
  const distanciaPromedio =
    distancias.length === 0
      ? null
      : distancias.reduce((suma, d) => suma + d, 0) / distancias.length;

  const estimadas = seleccionadas.filter((f) => f.ubicacion_estimada).length;

  return (
    /* EL PANEL ACOMPAÑA AL SCROLL, y su tope de alto no es prolijidad: es lo
       que mantiene alcanzable al botón que cierra el paso.

       El panel mide unos 800 px con sus dos avisos puestos, y el equipo trabaja
       contra un viewport que rinde como 800×600 (ver `webapp/CLAUDE.md`), así
       que entra en la pantalla salvo en monitores altos. Pegado y sin tope, lo
       que sobra queda abajo del borde para siempre: pegado ya no se mueve con
       la página, y sin scroll propio no hay cómo llegar a "Agregar al informe".

       `100cqh` es el alto de `main` —el contenedor de scroll, declarado en
       `MainLayout`— y no el de la ventana: `100svh` se pasaría por lo que mide
       la barra superior, que es justo el margen que deja el botón afuera. Se le
       restan los `top-5` de arriba y el mismo aire abajo.

       Todo va con `lg:` porque abajo de ese ancho el panel deja de ser columna
       y cae después del listado: ahí un alto máximo encerraría su contenido en
       una caja chica sin ningún motivo. */
    <aside className="scroll-fino flex flex-col gap-3.5 lg:sticky lg:top-5 lg:max-h-[calc(100cqh-2.5rem)] lg:overflow-y-auto">
      {/* El resumen, sobre fondo oscuro: es la única cifra grande de la
          pantalla y el contraste la separa del listado.

          `superficie-fija` y NO `bg-ink`: aquel se INVIERTE en modo oscuro
          (#17181b -> #f6f6f5) y dejaba este panel blanco sobre blanco, con un
          contraste medido de 1.08 contra el 4.5 que pide la WCAG. El diseño lo
          quiere oscuro siempre, y por eso el token tampoco se invierte. */}
      <div className="bg-superficie-fija text-superficie-fija-foreground rounded-2xl p-4">
        <div className="text-superficie-fija-muted flex items-center justify-between gap-2 text-[0.6875rem] font-extrabold tracking-[0.08em] uppercase">
          <span>Valor de referencia</span>
          {/* "0 de 0" mientras el catálogo viaja afirmaría que no hay nada que
              elegir, así que el contador espera a que haya filtro que contar. */}
          {totalDelFiltro > 0 && (
            <span className="text-superficie-fija-ghost font-mono tabular-nums">
              {seleccionadas.length} de {numero(totalDelFiltro)}
            </span>
          )}
        </div>

        {/* La cifra y su unidad en la MISMA línea, alineadas por su base: en dos
            renglones la unidad se lee como una bajada y la cifra queda sin
            decir de qué es. */}
        <div className="mt-3 flex items-baseline gap-2.5">
          <span className="font-mono text-[1.6875rem] leading-none font-bold tracking-[-0.03em] tabular-nums">
            {mediaUfHa === null ? "—" : numero(mediaUfHa, 0)}
          </span>
          <span className="text-superficie-fija-muted text-[0.78125rem] font-bold">
            UF/ha media
          </span>
        </div>

        <p className="text-superficie-fija-dim mt-1.5 text-xs font-semibold">
          Media de las seleccionadas, sin ajustes de homologación
        </p>

        <dl className="border-superficie-fija-linea mt-4 flex flex-col gap-2 border-t pt-3.5 text-[0.78125rem] font-semibold">
          <Fila
            rotulo="Rango UF/ha"
            valor={
              conValor.length === 0
                ? "—"
                : `${numero(Math.min(...conValor), 0)} – ${numero(Math.max(...conValor), 0)}`
            }
          />
          <Fila
            rotulo="Superficie total"
            valor={
              seleccionadas.length === 0
                ? "—"
                : `${hectareas(
                    seleccionadas.reduce((suma, f) => suma + (f.superficie_terreno_m2 ?? 0), 0),
                  )} ha`
            }
          />
          <Fila
            rotulo="Valor total del predio"
            valor={valorTotalUf === null ? "—" : `${numero(valorTotalUf, 0)} UF`}
            // El verde marca que es una cifra DERIVADA y no un dato leído del
            // catálogo, que es lo que son las otras tres.
            className={valorTotalUf === null ? undefined : "text-superficie-fija-exito"}
          />
          <Fila rotulo="Distancia promedio" valor={distancia(distanciaPromedio)} />
          <Fila
            rotulo="Datos validados"
            valor={
              seleccionadas.length === 0
                ? "—"
                : `${seleccionadasValidadas} de ${seleccionadas.length}`
            }
            // Verde cuando no falta ninguna, ámbar cuando sí. Es la misma
            // lectura que el aviso de abajo, en un renglón: quien mira las
            // cifras no tiene que bajar para saber si están todas.
            className={
              seleccionadas.length === 0
                ? undefined
                : seleccionadasSinValidar === 0
                  ? "text-superficie-fija-exito"
                  : "text-superficie-fija-aviso"
            }
          />
        </dl>

        {seleccionadasSinValidar > 0 && (
          // ADVIERTE Y NO BLOQUEA. Validar es criterio del tasador, no una
          // regla del sistema: hay comparables que se llevan al informe
          // justamente para discutirlos. Impedir el "Agregar" convertiría una
          // marca de revisión en un permiso.
          <div className="bg-superficie-fija-aviso-fondo text-superficie-fija-aviso mt-3.5 flex gap-2.5 rounded-xl px-3.5 py-3 text-[0.78125rem] leading-snug font-semibold text-pretty">
            <TriangleAlert className="mt-px size-4 flex-none" aria-hidden />
            <span>
              Hay seleccionadas sin validar: revise los datos antes de llevarlas al informe.
            </span>
          </div>
        )}

        {estimadas > 0 && (
          // No es un error ni bloquea nada: es lo que hay que saber al leer la
          // distancia promedio de arriba, que con centroides no es una medición.
          // Va en CAJA y no como texto suelto porque compite con cuatro cifras
          // alineadas: sin fondo se lee como una quinta fila que se desbordó.
          <div className="bg-superficie-fija-aviso-fondo text-superficie-fija-aviso mt-3.5 flex gap-2.5 rounded-xl px-3.5 py-3 text-[0.78125rem] leading-snug font-semibold text-pretty">
            <TriangleAlert className="mt-px size-4 flex-none" aria-hidden />
            <span>
              {estimadas === 1
                ? "1 seleccionada tiene solo el centro de su comuna: su distancia es estimada."
                : `${estimadas} seleccionadas tienen solo el centro de su comuna: su distancia es estimada.`}
            </span>
          </div>
        )}
      </div>

      {/* La lista de lo elegido, para poder sacar algo sin buscarlo de nuevo
          en la grilla. */}
      <div className="border-border rounded-2xl border-[1.5px] px-4 pt-3.5 pb-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-ink-hint text-[0.6875rem] font-extrabold tracking-[0.08em] uppercase">
            Seleccionadas
          </p>
          {seleccionadas.length > 0 && (
            <Button variant="ghost" size="sm" onClick={onVaciar}>
              Vaciar
            </Button>
          )}
        </div>

        {seleccionadas.length === 0 ? (
          <p className="text-ink-hint mt-2.5 text-[0.78125rem] leading-snug font-semibold text-pretty">
            Aún no ha seleccionado ninguna. Marque fichas de la lista o haga clic en los
            puntos del gráfico.
          </p>
        ) : (
          // UN `div` CON `overflow-y-auto`, NO EL `ScrollArea` DEL DS, y ese
          // cambio es el que arregla el desborde de la captura. El viewport de
          // Radix es `size-full` sobre un contenedor sin alto propio, así que
          // un `max-h` en la raíz no lo contiene: la lista crecía fuera de su
          // tarjeta y el botón de abajo le quedaba encima. Acá el alto sí lo
          // fija el contenedor que scrollea.
          <ul className="scroll-fino mt-3 flex max-h-[10.25rem] flex-col gap-2 overflow-y-auto">
            {seleccionadas.map((ficha) => (
              <li
                key={`${ficha.clase}:${ficha.origen_id}`}
                className="border-border bg-superficie-sutil flex items-center gap-2.5 rounded-xl border-[1.5px] px-3 py-2.5"
              >
                {/* La FORMA distingue la clase, igual que en el gráfico y en el
                    mapa: cuadrado para una transacción, círculo para una
                    oferta. Si fuera solo color se perdería en blanco y negro. */}
                <span
                  className={cn(
                    "size-[0.4375rem] flex-none",
                    ficha.clase === "transaccion"
                      ? "bg-info-dot rounded-[0.125rem]"
                      : "bg-warning-dot rounded-full",
                  )}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-ink truncate text-[0.78125rem] font-bold">
                    {ficha.titulo}
                  </p>
                  <p className="text-ink-hint mt-0.5 font-mono text-[0.6875rem] font-semibold">
                    {hectareas(ficha.superficie_terreno_m2)} ha ·{" "}
                    {numero(ficha.uf_ha_terreno, 0)} UF/ha · {distancia(ficha.distancia_m)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onQuitar(ficha)}
                  aria-label={`Quitar ${ficha.titulo}`}
                  className="text-ink-ghost hover:text-danger-dot flex-none"
                >
                  <X className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* El botón va en SU PROPIA TARJETA y no suelto bajo la lista: es la
          acción que cierra el paso, y sin caja quedaba pegado al borde de la
          lista como si fuera su última fila. */}
      <div className="border-border flex flex-col gap-2 rounded-2xl border-[1.5px] px-4 pt-3.5 pb-4">
        {/* Sin alto propio: el diseño pide 42 px y el `h-11` del DS rinde
            41,25 con la perilla de densidad. Fijarlo a mano sería sacar el
            botón de la única perilla que gobierna el tamaño del sistema. */}
        <Button
          className="w-full"
          disabled={seleccionadas.length === 0 || agregando}
          onClick={onAgregar}
        >
          {agregando ? (
            "Agregando…"
          ) : (
            <>
              <Plus className="size-4" aria-hidden />
              Agregar {seleccionadas.length} al informe
            </>
          )}
        </Button>

        <p className="text-ink-hint flex items-start gap-2.5 px-0.5 pt-1 text-xs font-semibold text-pretty">
          <Info className="mt-0.5 size-3.5 flex-none" aria-hidden />
          <span>
            Van al capítulo de comparación con mercado del informe, donde se homologan.
          </span>
        </p>

        {yaEnlazadas > 0 && (
          // El conteo ES el acceso a la otra pantalla, y no un texto suelto
          // más un enlace al lado: el número es justamente el motivo por el
          // que alguien querría ir a revisarlas.
          <p className="text-ink-muted px-0.5 text-xs font-semibold text-pretty">
            El informe ya tiene{" "}
            <Link to={`/referencias/${informeId}/enlazadas`} className="text-primary underline">
              {yaEnlazadas} {yaEnlazadas === 1 ? "referencia" : "referencias"}
            </Link>
            : las nuevas se suman a las existentes.
          </p>
        )}
      </div>
    </aside>
  );
}

function Fila({
  rotulo,
  valor,
  className,
}: {
  rotulo: string;
  valor: string;
  className?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-superficie-fija-rotulo">{rotulo}</dt>
      <dd className={cn("font-mono tabular-nums", className)}>{valor}</dd>
    </div>
  );
}
